// UserHeader.tsx

import React, { useEffect, useRef, useState } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  useTheme,
  useMediaQuery,
  Drawer,
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Avatar,
  Divider,
  Typography,
} from "@mui/material";

import { useNavigate, useLocation } from "react-router-dom";

import DashboardIcon from "@mui/icons-material/Dashboard";
import PeopleIcon from "@mui/icons-material/People";
import WorkIcon from "@mui/icons-material/Work";
import PaymentIcon from "@mui/icons-material/Payment";
import SchoolIcon from "@mui/icons-material/School";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import SettingsIcon from "@mui/icons-material/Settings";
import GroupIcon from "@mui/icons-material/Group";
import EventIcon from "@mui/icons-material/Event";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import QuizIcon from "@mui/icons-material/Quiz";
import UpdateIcon from "@mui/icons-material/Update";
import LogoutIcon from "@mui/icons-material/Logout";
import CloseIcon from "@mui/icons-material/Close";
import StorefrontIcon from "@mui/icons-material/Storefront";

import AvatarComponent from "../components/Avatar";

import GrowniverseLogoFull from "../Photos/Logo/growniverse-logo-full.svg";

import "../components/WelcomeSection.css";
import "./Design/Dashboard.css";

// ============================================================
// TYPES
// ============================================================

type NavItem = {
  label: string;
  path: string;
  icon?: React.ReactNode;
  adminOnly?: boolean;
  requiresAffiliate?: boolean;
};

// ============================================================
// USER HEADER
// ============================================================

const UserHeader: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const theme = useTheme();

  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  // ============================================================
  // STATE
  // ============================================================

  const mounted = useRef(false);

  const [userName, setUserName] = useState("");
  const [profileImage, setProfileImage] = useState<string | null>(null);

  // STATUS
  const [status, setStatus] = useState<number>(0);

  const [isAdmin, setIsAdmin] = useState<boolean>(false);
  const [growniverseId, setGrowniverseId] = useState<string>("");
  const [affiliateApproved, setAffiliateApproved] =
    useState<boolean>(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] =
    useState<null | HTMLElement>(null);

  const menuOpen = Boolean(anchorEl);

  // ============================================================
  // LOAD USER
  // ============================================================

  useEffect(() => {
    if (mounted.current) return;

    const appKey = localStorage.getItem("appkey");

    if (!appKey || appKey === "NeedVerification") {
      window.location.href = "/";
      return;
    }

    try {
      const storedProfile =
        localStorage.getItem("userProfile");

      if (storedProfile) {
        const userProfile = JSON.parse(storedProfile);

        setUserName(
          userProfile.fullName ?? ""
        );

        setProfileImage(
          userProfile.profileImage ?? null
        );

        // ======================================================
        // STATUS
        // status === 2 -> navigation hidden
        // ======================================================

        setStatus(
          Number(userProfile.status ?? 0)
        );

        setIsAdmin(
          Boolean(userProfile.isAdmin)
        );

        setGrowniverseId(
          String(
            userProfile.growniverseId ?? ""
          )
        );

        setAffiliateApproved(
          Boolean(
            userProfile.affiliateApproved
          )
        );
      }
    } catch (error) {
      console.warn(
        "Invalid userProfile in localStorage",
        error
      );
    }

    mounted.current = true;
  }, []);

  // ============================================================
  // PROFILE MENU
  // ============================================================

  const handleAvatarClick = (
    event: React.MouseEvent<HTMLElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const goTo = (path: string) => {
    handleClose();
    navigate(path);
  };

  // ============================================================
  // LOGOUT
  // ============================================================

  const handleLogout = () => {
    handleClose();

    localStorage.clear();

    window.location.href = "/";
  };

  // ============================================================
  // DRAWER NAVIGATION
  // ============================================================

  const handleNavigate = (path: string) => {
    navigate(path);
    setDrawerOpen(false);
  };

  // ============================================================
  // NAVIGATION LIST
  // ============================================================

  const navList: NavItem[] = [
    {
      label: "Dashboard",
      path: "/dashboard",
      icon: <DashboardIcon />,
      requiresAffiliate: true,
    },

    {
      label: "Payouts",
      path: "/payouts",
      icon: <PaymentIcon />,
      requiresAffiliate: true,
    },

    {
      label: "My Kyc",
      path: "/kyc",
      icon: <PeopleIcon />,
      requiresAffiliate: true,
    },

    {
      label: "Training",
      path: "/training",
      icon: <SchoolIcon />,
      requiresAffiliate: true,
    },

    {
      label: "Wallet",
      path: "/mywallet",
      icon: <PaymentIcon />,
      requiresAffiliate: true,
    },

    {
      label: "My Courses",
      path: "/mycourses",
      icon: <WorkIcon />,
    },

    {
      label: "Profile",
      path: "/profile",
      icon: <PeopleIcon />,
    },

    {
      label: "Certification",
      path: "/certification",
      icon: <SchoolIcon />,
      requiresAffiliate: false,
    },

    {
      label: "Support",
      path: "/support",
      icon: <SupportAgentIcon />,
    },
    {
      label: "Course Store",
      path: "/store",
      icon: <StorefrontIcon />,
    },

    // ========================================================
    // ADMIN
    // ========================================================

    {
      label: "Package",
      path: "/packagepayment",
      icon: <PaymentIcon />,
      adminOnly: true,
    },

    {
      label: "Internship",
      path: "/internship",
      icon: <WorkIcon />,
      adminOnly: true,
    },

    {
      label: "Turnover",
      path: "/admin",
      icon: <DashboardIcon />,
      adminOnly: true,
    },

    {
      label: "Kyc",
      path: "/kycdetail",
      icon: <PeopleIcon />,
      adminOnly: true,
    },

    {
      label: "WithDraw",
      path: "/withdrawrequests",
      icon: <PaymentIcon />,
      adminOnly: true,
    },

    {
      label: "Users",
      path: "/userdetail",
      icon: <PeopleIcon />,
      adminOnly: true,
    },

    {
      label: "Update User",
      path: "/updateuser",
      icon: <UpdateIcon />,
      adminOnly: true,
    },

    {
      label: "Courses",
      path: "/admincourses",
      icon: <SchoolIcon />,
      adminOnly: true,
    },

    {
      label: "Wallet (Admin)",
      path: "/adminwallet",
      icon: <PaymentIcon />,
      adminOnly: true,
    },

    {
      label: "Team",
      path: "/qualityUser",
      icon: <GroupIcon />,
      adminOnly: true,
    },

    {
      label: "MCQ",
      path: "/courseQuestion",
      icon: <QuizIcon />,
      adminOnly: true,
    },

    {
      label: "Coupon",
      path: "/couponCode",
      icon: <LocalOfferIcon />,
      adminOnly: true,
    },

    {
      label: "Event",
      path: "/eventandpayment",
      icon: <EventIcon />,
      adminOnly: true,
    },

    {
      label: "Customized",
      path: "/customized",
      icon: <SettingsIcon />,
      adminOnly: true,
    },

    {
      label: "Notification",
      path: "/notification",
      icon: <SettingsIcon />,
      adminOnly: true,
    },
  ];

  // ============================================================
  // PROFILE IMAGE
  // ============================================================

  const renderProfileImage = (size: number) => {
    if (profileImage) {
      return (
        <img
          src={`data:image/jpeg;base64,${profileImage}`}
          alt="Profile"
          style={{
            width: size,
            height: size,
            borderRadius: "50%",
            objectFit: "cover",
            display: "block",
          }}
        />
      );
    }

    return (
      <Avatar
        sx={{
          width: size,
          height: size,

          background:
            "linear-gradient(135deg, #00f6ff, #8b5cff)",

          color: "#031014",

          fontWeight: 900,

          fontSize: size * 0.42,
        }}
      >
        {userName
          ?.trim()
          ?.charAt(0)
          ?.toUpperCase() || "U"}
      </Avatar>
    );
  };

  // ============================================================
  // NAV ITEM
  // ============================================================

  const renderListItem = (
    item: NavItem
  ) => {

    // ========================================================
    // STATUS CHECK
    //
    // status === 2 -> don't show navigation
    // status != 2 -> show navigation
    // ========================================================

    if (status === 2) {
      return null;
    }

    // ========================================================
    // ADMIN CHECK
    // ========================================================

    if (
      item.adminOnly &&
      !isAdmin
    ) {
      return null;
    }

    // ========================================================
    // AFFILIATE CHECK
    // ========================================================

    if (
      item.requiresAffiliate &&
      !affiliateApproved
    ) {
      return null;
    }

    const active =
      location.pathname === item.path;

    return (
      <ListItemButton
        key={item.path}
        onClick={() =>
          handleNavigate(item.path)
        }
        sx={{
          position: "relative",

          overflow: "hidden",

          minHeight: 50,

          mb: 0.8,

          px: 1.5,

          borderRadius: "10px",

          color: active
            ? "#ffffff"
            : "#aeb9c8",

          border: active
            ? "1px solid rgba(0,246,255,0.20)"
            : "1px solid transparent",

          background: active
            ? "linear-gradient(90deg, rgba(0,246,255,0.12), rgba(139,92,255,0.08))"
            : "transparent",

          transition:
            "all 0.22s ease",

          "&:hover": {
            transform:
              "translateX(5px)",

            background:
              "linear-gradient(90deg, rgba(0,246,255,0.09), rgba(139,92,255,0.06))",

            borderColor:
              "rgba(0,246,255,0.15)",

            color:
              "#ffffff",
          },

          "&::before": active
            ? {
                content: '""',

                position:
                  "absolute",

                left: 0,

                top: "20%",

                bottom: "20%",

                width: 3,

                borderRadius:
                  "0 5px 5px 0",

                background:
                  "#00f6ff",

                boxShadow:
                  "0 0 12px #00f6ff",
              }
            : {},
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 40,

            color: active
              ? "#00f6ff"
              : "#8f9dad",

            transition:
              "all 0.2s ease",

            "& svg": {
              fontSize: 21,
            },
          }}
        >
          {item.icon}
        </ListItemIcon>

        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            fontSize:
              "13px",

            fontWeight:
              active
                ? 750
                : 550,

            color:
              active
                ? "#ffffff"
                : "#c3ccd8",
          }}
        />

        {active && (
          <span
            style={{
              width: 5,

              height: 5,

              borderRadius:
                "50%",

              background:
                "#00f6ff",

              boxShadow:
                "0 0 10px #00f6ff",
            }}
          />
        )}
      </ListItemButton>
    );
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <>
      {/* ======================================================
          MAIN HEADER
      ====================================================== */}

      <header
        style={{
          position:
            "relative",

          zIndex: 1200,

          width:
            "100%",

          minHeight:
            isXs
              ? 70
              : isSm
              ? 82
              : 92,

          display:
            "flex",

          alignItems:
            "center",

          background:
            "linear-gradient(135deg, rgba(5,8,16,0.98), rgba(8,12,24,0.96))",

          borderBottom:
            "1px solid rgba(0,246,255,0.16)",

          boxShadow:
            "0 8px 35px rgba(0,0,0,0.45), inset 0 -1px 0 rgba(255,255,255,0.025)",

          backdropFilter:
            "blur(20px)",

          overflow:
            "hidden",
        }}
      >

        {/* ====================================================
            BACKGROUND GRID
        ==================================================== */}

        <div
          style={{
            position:
              "absolute",

            inset: 0,

            pointerEvents:
              "none",

            opacity:
              0.08,

            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",

            backgroundSize:
              "35px 35px",
          }}
        />

        {/* ====================================================
            CYAN GLOW
        ==================================================== */}

        <div
          style={{
            position:
              "absolute",

            width: 260,

            height: 260,

            left:
              -120,

            top:
              -180,

            borderRadius:
              "50%",

            background:
              "rgba(0,246,255,0.09)",

            filter:
              "blur(85px)",

            pointerEvents:
              "none",
          }}
        />

        {/* ====================================================
            PURPLE GLOW
        ==================================================== */}

        <div
          style={{
            position:
              "absolute",

            width: 280,

            height: 280,

            right:
              -130,

            top:
              -180,

            borderRadius:
              "50%",

            background:
              "rgba(139,92,255,0.10)",

            filter:
              "blur(90px)",

            pointerEvents:
              "none",
          }}
        />

        {/* ====================================================
            HEADER CONTENT
        ==================================================== */}

        <div
          style={{
            position:
              "relative",

            zIndex: 5,

            width:
              "100%",

            minHeight:
              isXs
                ? 70
                : isSm
                ? 82
                : 92,

            display:
              "flex",

            alignItems:
              "center",

            padding:
              isXs
                ? "0 10px"
                : isSm
                ? "0 18px"
                : "0 28px",

            boxSizing:
              "border-box",
          }}
        >

          {/* ==================================================
              LEFT : MENU
          ================================================== */}

          <div
            style={{
              position:
                "relative",

              zIndex: 3,

              display:
                "flex",

              alignItems:
                "center",

              flexShrink:
                0,
            }}
          >
            <Tooltip
              title="Open navigation"
              arrow
            >
              <IconButton
                onClick={() =>
                  setDrawerOpen(true)
                }
                aria-label="Open navigation"
                sx={{
                  width:
                    isXs
                      ? 42
                      : isSm
                      ? 48
                      : 50,

                  height:
                    isXs
                      ? 42
                      : isSm
                      ? 48
                      : 50,

                  color:
                    "#dce7f3",

                  border:
                    "1px solid rgba(0,246,255,0.20)",

                  borderRadius:
                    "10px",

                  background:
                    "rgba(0,246,255,0.035)",

                  transition:
                    "all 0.25s ease",

                  "&:hover": {
                    color:
                      "#00f6ff",

                    background:
                      "rgba(0,246,255,0.09)",

                    borderColor:
                      "rgba(0,246,255,0.45)",

                    boxShadow:
                      "0 0 18px rgba(0,246,255,0.15)",

                    transform:
                      "translateY(-1px)",
                  },

                  "&:active": {
                    transform:
                      "scale(0.95)",
                  },
                }}
              >
                <div
                  style={{
                    width:
                      isXs
                        ? 20
                        : 22,

                    display:
                      "flex",

                    flexDirection:
                      "column",

                    gap:
                      5,
                  }}
                >
                  <span
                    style={{
                      width:
                        "100%",

                      height:
                        2,

                      borderRadius:
                        5,

                      background:
                        "currentColor",

                      boxShadow:
                        "0 0 6px rgba(0,246,255,0.35)",
                    }}
                  />

                  <span
                    style={{
                      width:
                        "100%",

                      height:
                        2,

                      borderRadius:
                        5,

                      background:
                        "currentColor",

                      boxShadow:
                        "0 0 6px rgba(0,246,255,0.35)",
                    }}
                  />

                  <span
                    style={{
                      width:
                        "100%",

                      height:
                        2,

                      borderRadius:
                        5,

                      background:
                        "currentColor",

                      boxShadow:
                        "0 0 6px rgba(0,246,255,0.35)",
                    }}
                  />
                </div>
              </IconButton>
            </Tooltip>
          </div>

          {/* ==================================================
              CENTER : PREMIUM SMOOTH WHITE LOGO
          ================================================== */}

          <div
            onClick={() =>
              navigate("/dashboard")
            }
            style={{
              position:
                "absolute",

              left:
                "50%",

              top:
                "50%",

              transform:
                "translate(-50%, -50%)",

              width:
                isXs
                  ? "150px"
                  : isSm
                  ? "215px"
                  : "270px",

              maxWidth:
                isXs
                  ? "calc(100vw - 110px)"
                  : "calc(100vw - 160px)",

              padding:
                isXs
                  ? "8px 12px"
                  : isSm
                  ? "10px 16px"
                  : "12px 20px",

              display:
                "flex",

              alignItems:
                "center",

              justifyContent:
                "center",

              cursor:
                "pointer",

              zIndex:
                2,

              background: `
                linear-gradient(
                  145deg,
                  rgba(255,255,255,0.96) 0%,
                  rgba(248,249,247,0.94) 45%,
                  rgba(238,241,239,0.90) 100%
                )
              `,

              border:
                "1px solid rgba(255,255,255,0.70)",

              borderRadius:
                "11px",

              boxShadow: `
                0 8px 24px rgba(0,0,0,0.32),
                0 2px 7px rgba(0,0,0,0.16),
                inset 0 1px 2px rgba(255,255,255,0.95),
                inset 0 -1px 1px rgba(0,0,0,0.06)
              `,

              overflow:
                "hidden",

              transition:
                "transform 0.25s ease, box-shadow 0.25s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform =
                "translate(-50%, -50%) scale(1.025)";

              e.currentTarget.style.boxShadow = `
                0 10px 30px rgba(0,0,0,0.36),
                0 3px 9px rgba(0,0,0,0.18),
                inset 0 1px 2px rgba(255,255,255,1),
                inset 0 -1px 1px rgba(0,0,0,0.05)
              `;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform =
                "translate(-50%, -50%) scale(1)";

              e.currentTarget.style.boxShadow = `
                0 8px 24px rgba(0,0,0,0.32),
                0 2px 7px rgba(0,0,0,0.16),
                inset 0 1px 2px rgba(255,255,255,0.95),
                inset 0 -1px 1px rgba(0,0,0,0.06)
              `;
            }}
          >

            {/* SOFT TOP REFLECTION */}

            <div
              style={{
                position:
                  "absolute",

                top:
                  0,

                left:
                  "12%",

                right:
                  "12%",

                height:
                  "2px",

                background:
                  "linear-gradient(90deg, transparent, rgba(255,255,255,0.95), transparent)",

                opacity:
                  0.8,

                pointerEvents:
                  "none",
              }}
            />

            {/* SUBTLE CENTER LIGHT */}

            <div
              style={{
                position:
                  "absolute",

                width:
                  "65%",

                height:
                  "100%",

                left:
                  "17.5%",

                background:
                  "rgba(255,255,255,0.18)",

                filter:
                  "blur(14px)",

                pointerEvents:
                  "none",
              }}
            />

            {/* LOGO */}

            <img
              src={GrowniverseLogoFull}
              alt="Growniverse"
              style={{
                position:
                  "relative",

                zIndex:
                  2,

                display:
                  "block",

                width:
                  "100%",

                height:
                  "auto",

                objectFit:
                  "contain",

                filter:
                  "drop-shadow(0 1px 1px rgba(0,0,0,0.20))",
              }}
            />
          </div>

          {/* ==================================================
              RIGHT SIDE
          ================================================== */}

          <div
            style={{
              marginLeft:
                "auto",

              display:
                "flex",

              alignItems:
                "center",

              gap:
                14,

              flexShrink:
                0,

              position:
                "relative",

              zIndex:
                3,
            }}
          >

            {/* SYSTEM STATUS */}

            {isMdUp && (
              <div
                style={{
                  padding:
                    "8px 14px",

                  border:
                    "1px solid rgba(0,246,255,0.16)",

                  background:
                    "rgba(0,246,255,0.035)",

                  display:
                    "flex",

                  alignItems:
                    "center",

                  gap:
                    8,
                }}
              >
                <span
                  style={{
                    width:
                      6,

                    height:
                      6,

                    borderRadius:
                      "50%",

                    background:
                      "#00f6ff",

                    boxShadow:
                      "0 0 9px #00f6ff",
                  }}
                />

                <span
                  style={{
                    color:
                      "#a8b5c5",

                    fontSize:
                      "9px",

                    fontWeight:
                      700,

                    letterSpacing:
                      "1.5px",

                    whiteSpace:
                      "nowrap",
                  }}
                >
                  {growniverseId ||
                    "SYSTEM ONLINE"}
                </span>
              </div>
            )}

            {/* PROFILE BUTTON */}

            <Tooltip
              title={
                userName ||
                "Account"
              }
              arrow
            >
              <IconButton
                onClick={
                  handleAvatarClick
                }
                aria-label="Open account menu"
                sx={{
                  width:
                    isXs
                      ? 44
                      : isSm
                      ? 50
                      : 54,

                  height:
                    isXs
                      ? 44
                      : isSm
                      ? 50
                      : 54,

                  padding:
                    "2px",

                  flexShrink:
                    0,

                  borderRadius:
                    "50%",

                  background:
                    "conic-gradient(#00f6ff, #8b5cff, #00f6ff)",

                  boxShadow:
                    "0 0 18px rgba(0,246,255,0.16)",

                  transition:
                    "all 0.25s ease",

                  "&:hover": {
                    boxShadow:
                      "0 0 28px rgba(0,246,255,0.30)",

                    transform:
                      "scale(1.05)",
                  },
                }}
              >
                <div
                  style={{
                    width:
                      "100%",

                    height:
                      "100%",

                    borderRadius:
                      "50%",

                    overflow:
                      "hidden",

                    background:
                      "#080c15",

                    border:
                      "2px solid #080c15",

                    display:
                      "flex",

                    alignItems:
                      "center",

                    justifyContent:
                      "center",
                  }}
                >
                  {profileImage ? (
                    <img
                      src={`data:image/jpeg;base64,${profileImage}`}
                      alt="Profile"
                      style={{
                        width:
                          "100%",

                        height:
                          "100%",

                        objectFit:
                          "cover",

                        display:
                          "block",
                      }}
                    />
                  ) : (
                    <AvatarComponent
                      name={userName}
                      size={
                        isXs
                          ? 34
                          : isSm
                          ? 40
                          : 44
                      }
                    />
                  )}
                </div>
              </IconButton>
            </Tooltip>
          </div>
        </div>

        {/* BOTTOM LINE */}

        <div
          style={{
            position:
              "absolute",

            bottom:
              0,

            left:
              "18%",

            right:
              "18%",

            height:
              1,

            background:
              "linear-gradient(90deg, transparent, rgba(0,246,255,0.45), transparent)",

            pointerEvents:
              "none",
          }}
        />
      </header>

      {/* ========================================================
          ACCOUNT MENU
      ======================================================== */}

      <Menu
        anchorEl={anchorEl}
        open={menuOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical:
            "bottom",
          horizontal:
            "right",
        }}
        transformOrigin={{
          vertical:
            "top",
          horizontal:
            "right",
        }}
        MenuListProps={{
          disablePadding:
            true,
        }}
        PaperProps={{
          sx: {
            mt:
              1.5,

            minWidth:
              isXs
                ? 220
                : 250,

            overflow:
              "hidden",

            bgcolor:
              "rgba(5,9,18,0.98)",

            color:
              "#ffffff",

            border:
              "1px solid rgba(0,246,255,0.20)",

            borderRadius:
              "12px",

            boxShadow:
              "0 20px 60px rgba(0,0,0,0.65), 0 0 30px rgba(0,246,255,0.06)",

            backdropFilter:
              "blur(20px)",

            "& .MuiMenuItem-root": {
              color:
                "#e8eef7",

              fontSize:
                "13px",

              fontWeight:
                600,

              padding:
                "13px 18px",

              borderBottom:
                "1px solid rgba(255,255,255,0.055)",

              transition:
                "all 0.2s ease",

              "&:hover": {
                background:
                  "rgba(0,246,255,0.08)",

                color:
                  "#00f6ff",
              },
            },

            "& .MuiMenuItem-root:last-child": {
              borderBottom:
                "none",
            },
          },
        }}
      >

        {/* ACCOUNT HEADER */}

        <div
          style={{
            padding:
              "17px 18px",

            borderBottom:
              "1px solid rgba(255,255,255,0.08)",

            background:
              "linear-gradient(135deg, rgba(0,246,255,0.07), rgba(139,92,255,0.05))",
          }}
        >
          <div
            style={{
              display:
                "flex",

              alignItems:
                "center",

              gap:
                "12px",
            }}
          >
            <div
              style={{
                width:
                  "42px",

                height:
                  "42px",

                minWidth:
                  "42px",

                padding:
                  "2px",

                borderRadius:
                  "50%",

                background:
                  "conic-gradient(#00f6ff, #8b5cff, #00f6ff)",
              }}
            >
              <div
                style={{
                  width:
                    "100%",

                  height:
                    "100%",

                  borderRadius:
                    "50%",

                  overflow:
                    "hidden",

                  background:
                    "#080c15",

                  display:
                    "flex",

                  alignItems:
                    "center",

                  justifyContent:
                    "center",
                }}
              >
                {profileImage ? (
                  <img
                    src={`data:image/jpeg;base64,${profileImage}`}
                    alt="Profile"
                    style={{
                      width:
                        "100%",

                      height:
                        "100%",

                      objectFit:
                        "cover",
                    }}
                  />
                ) : (
                  <AvatarComponent
                    name={userName}
                    size={34}
                  />
                )}
              </div>
            </div>

            <div
              style={{
                minWidth:
                  0,
              }}
            >
              <div
                style={{
                  color:
                    "#ffffff",

                  fontSize:
                    "14px",

                  fontWeight:
                    700,

                  overflow:
                    "hidden",

                  textOverflow:
                    "ellipsis",

                  whiteSpace:
                    "nowrap",

                  maxWidth:
                    "150px",
                }}
              >
                {userName ||
                  "User"}
              </div>

              <div
                style={{
                  marginTop:
                    "4px",

                  display:
                    "flex",

                  alignItems:
                    "center",

                  gap:
                    "6px",

                  color:
                    "#8d9aaa",

                  fontSize:
                    "9px",

                  fontWeight:
                    700,

                  letterSpacing:
                    "1.5px",
                }}
              >
                <span
                  style={{
                    width:
                      "6px",

                    height:
                      "6px",

                    borderRadius:
                      "50%",

                    background:
                      "#00f6ff",

                    boxShadow:
                      "0 0 8px #00f6ff",
                  }}
                />

                ONLINE
              </div>
            </div>
          </div>
        </div>

        {/* PROFILE */}

        <MenuItem
          onClick={() =>
            goTo("/profile")
          }
        >
          <span
            style={{
              width:
                "28px",

              color:
                "#00f6ff",

              fontSize:
                "17px",

              marginRight:
                "10px",
            }}
          >
            ◎
          </span>

          Profile
        </MenuItem>

        {/* SUPPORT */}

        <MenuItem
          onClick={() =>
            goTo("/support")
          }
        >
          <span
            style={{
              width:
                "28px",

              color:
                "#8b7cff",

              fontSize:
                "17px",

              marginRight:
                "10px",
            }}
          >
            ?
          </span>

          Support
        </MenuItem>

        {/* LOGOUT */}

        <MenuItem
          onClick={
            handleLogout
          }
          sx={{
            color:
              "#ff8e9b !important",

            "&:hover": {
              background:
                "rgba(255,80,100,0.08) !important",

              color:
                "#ff6678 !important",
            },
          }}
        >
          <span
            style={{
              width:
                "28px",

              color:
                "#ff7182",

              fontSize:
                "17px",

              marginRight:
                "10px",
            }}
          >
            ↪
          </span>

          Logout
        </MenuItem>
      </Menu>

      {/* ========================================================
          NAVIGATION DRAWER
      ======================================================== */}

      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() =>
          setDrawerOpen(false)
        }
        PaperProps={{
          sx: {
            width: {
              xs: "88vw",
              sm: 350,
            },

            maxWidth:
              350,

            color:
              "#ffffff",

            background:
              "linear-gradient(180deg, #050912 0%, #070b15 55%, #05070d 100%)",

            borderRight:
              "1px solid rgba(0,246,255,0.18)",

            boxShadow:
              "15px 0 60px rgba(0,0,0,0.55)",

            overflow:
              "hidden",
          },
        }}
      >

        {/* ====================================================
            DRAWER GRID
        ==================================================== */}

        <Box
          sx={{
            position:
              "absolute",

            inset:
              0,

            pointerEvents:
              "none",

            opacity:
              0.07,

            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)",

            backgroundSize:
              "35px 35px",
          }}
        />

        {/* DRAWER GLOW */}

        <Box
          sx={{
            position:
              "absolute",

            width:
              260,

            height:
              260,

            top:
              -160,

            left:
              -100,

            borderRadius:
              "50%",

            background:
              "rgba(0,246,255,0.08)",

            filter:
              "blur(90px)",

            pointerEvents:
              "none",
          }}
        />

        {/* ====================================================
            DRAWER HEADER
        ==================================================== */}

        <Box
          sx={{
            position:
              "relative",

            zIndex:
              2,

            p: {
              xs: 2,
              sm: 2.5,
            },

            display:
              "flex",

            alignItems:
              "center",

            justifyContent:
              "space-between",
          }}
        >

          <Box
            sx={{
              display:
                "flex",

              alignItems:
                "center",

              gap:
                1.5,
            }}
          >

            {/* DRAWER PROFILE */}

            <Box
              sx={{
                width:
                  50,

                height:
                  50,

                padding:
                  "2px",

                borderRadius:
                  "50%",

                background:
                  "conic-gradient(#00f6ff, #8b5cff, #00f6ff)",

                boxShadow:
                  "0 0 20px rgba(0,246,255,0.16)",
              }}
            >
              <Box
                sx={{
                  width:
                    "100%",

                  height:
                    "100%",

                  borderRadius:
                    "50%",

                  overflow:
                    "hidden",

                  background:
                    "#080c15",

                  display:
                    "flex",

                  alignItems:
                    "center",

                  justifyContent:
                    "center",

                  border:
                    "2px solid #080c15",
                }}
              >
                {renderProfileImage(
                  42
                )}
              </Box>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize:
                    "15px",

                  fontWeight:
                    800,

                  color:
                    "#ffffff",
                }}
              >
                Growniverse
              </Typography>

              <Typography
                sx={{
                  mt:
                    0.4,

                  fontSize:
                    "9px",

                  color:
                    "#8d9aaa",

                  fontWeight:
                    700,

                  letterSpacing:
                    "1.5px",
                }}
              >
                {growniverseId ||
                  "USER"}
              </Typography>
            </Box>
          </Box>

          {/* CLOSE */}

          <IconButton
            onClick={() =>
              setDrawerOpen(false)
            }
            sx={{
              width:
                40,

              height:
                40,

              color:
                "#aebccc",

              border:
                "1px solid rgba(255,255,255,0.08)",

              borderRadius:
                "9px",

              "&:hover": {
                color:
                  "#00f6ff",

                borderColor:
                  "rgba(0,246,255,0.35)",

                background:
                  "rgba(0,246,255,0.07)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* ====================================================
            USER STATUS
        ==================================================== */}

        <Box
          sx={{
            position:
              "relative",

            zIndex:
              2,

            mx:
              2,

            mb:
              2,

            p:
              1.5,

            border:
              "1px solid rgba(0,246,255,0.14)",

            background:
              "linear-gradient(90deg, rgba(0,246,255,0.055), rgba(139,92,255,0.035))",
          }}
        >
          <Box
            sx={{
              display:
                "flex",

              alignItems:
                "center",

              justifyContent:
                "space-between",
            }}
          >
            <Box>
              <Typography
                sx={{
                  color:
                    "#ffffff",

                  fontSize:
                    "12px",

                  fontWeight:
                    700,
                }}
              >
                {userName ||
                  "User"}
              </Typography>

              <Typography
                sx={{
                  mt:
                    0.4,

                  color:
                    "#7f8da0",

                  fontSize:
                    "9px",
                }}
              >
                Account control panel
              </Typography>
            </Box>

            <Box
              sx={{
                display:
                  "flex",

                alignItems:
                  "center",

                gap:
                  0.6,

                color:
                  "#00f6ff",

                fontSize:
                  "8px",

                fontWeight:
                  800,

                letterSpacing:
                  "1px",
              }}
            >
              <Box
                sx={{
                  width:
                    6,

                  height:
                    6,

                  borderRadius:
                    "50%",

                  background:
                    "#00f6ff",

                  boxShadow:
                    "0 0 9px #00f6ff",
                }}
              />

              ONLINE
            </Box>
          </Box>
        </Box>

        <Divider
          sx={{
            position:
              "relative",

            zIndex:
              2,

            borderColor:
              "rgba(255,255,255,0.07)",
          }}
        />

        {/* ====================================================
            NAVIGATION TITLE
        ==================================================== */}

        <Box
          sx={{
            position:
              "relative",

            zIndex:
              2,

            px:
              2,

            pt:
              2.5,

            pb:
              1,
          }}
        >
          <Typography
            sx={{
              color:
                "#00f6ff",

              fontSize:
                "9px",

              fontWeight:
                800,

              letterSpacing:
                "3px",
            }}
          >
            NAVIGATION
          </Typography>
        </Box>

        {/* ====================================================
            NAVIGATION LIST
        ==================================================== */}

        <Box
          sx={{
            position:
              "relative",

            zIndex:
              2,

            px:
              1.5,

            overflowY:
              "auto",

            flex:
              1,

            "&::-webkit-scrollbar": {
              width:
                "4px",
            },

            "&::-webkit-scrollbar-thumb": {
              background:
                "rgba(0,246,255,0.25)",

              borderRadius:
                "10px",
            },
          }}
        >

          <List disablePadding>
            {navList.map(
              (item) =>
                renderListItem(
                  item
                )
            )}
          </List>

          {/* ==================================================
              LOGOUT
          ================================================== */}

          <Divider
            sx={{
              my:
                2,

              borderColor:
                "rgba(255,255,255,0.07)",
            }}
          />

          <List disablePadding>
            <ListItemButton
              onClick={
                handleLogout
              }
              sx={{
                minHeight:
                  50,

                borderRadius:
                  "10px",

                border:
                  "1px solid rgba(255,100,120,0.10)",

                background:
                  "rgba(255,80,100,0.025)",

                color:
                  "#ff9ca8",

                transition:
                  "all 0.2s ease",

                "&:hover": {
                  background:
                    "rgba(255,80,100,0.08)",

                  borderColor:
                    "rgba(255,100,120,0.25)",

                  transform:
                    "translateX(5px)",
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth:
                    40,

                  color:
                    "#ff7182",
                }}
              >
                <LogoutIcon />
              </ListItemIcon>

              <ListItemText
                primary="Logout"
                primaryTypographyProps={{
                  fontSize:
                    "13px",

                  fontWeight:
                    700,

                  color:
                    "#ff9ca8",
                }}
              />
            </ListItemButton>
          </List>
        </Box>

        {/* ====================================================
            DRAWER FOOTER
        ==================================================== */}

        <Box
          sx={{
            position:
              "relative",

            zIndex:
              2,

            p:
              2,

            borderTop:
              "1px solid rgba(255,255,255,0.07)",

            background:
              "rgba(0,0,0,0.15)",
          }}
        >
          <Box
            sx={{
              display:
                "flex",

              justifyContent:
                "space-between",

              alignItems:
                "center",
            }}
          >
            <Typography
              sx={{
                color:
                  "#596678",

                fontSize:
                  "8px",

                fontWeight:
                  700,

                letterSpacing:
                  "1.5px",
              }}
            >
              GROWNIVERSE SYSTEM
            </Typography>

            <Typography
              sx={{
                color:
                  "#596678",

                fontSize:
                  "8px",

                fontWeight:
                  700,
              }}
            >
              v1.0
            </Typography>
          </Box>

          <Box
            sx={{
              mt:
                1,

              display:
                "flex",

              alignItems:
                "center",

              gap:
                0.7,
            }}
          >
            <Box
              sx={{
                width:
                  5,

                height:
                  5,

                borderRadius:
                  "50%",

                background:
                  "#00f6ff",

                boxShadow:
                  "0 0 8px #00f6ff",
              }}
            />

            <Typography
              sx={{
                color:
                  "#657386",

                fontSize:
                  "8px",
              }}
            >
              SECURE CONNECTION
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default UserHeader;